import React from "react";
import ResearchArticle, {
  Body,
  Figure,
  List,
  Notes,
  Points,
  Section,
  Standout,
} from "../components/portfolio/ResearchArticle";
import { relatedArticles } from "../data/articles";
import { phoneStage } from "../data/articleStages";
import imgCheckoutLoops from "../assets/images/checkout-loops.webp";
import imgCheckoutLoopsNarrow from "../assets/images/checkout-loops-narrow.webp";
import imgMerchantSite from "../assets/images/homepage/checkout/merchant-site.webp";
import imgAll from "../assets/images/homepage/checkout/all.webp";
import imgVoucher from "../assets/images/homepage/checkout/voucher.webp";
import imgVoucherDetail from "../assets/images/homepage/checkout/voucher-detail.webp";
import imgThemedAll from "../assets/images/homepage/checkout/themed-all.webp";
import imgThemedOpen from "../assets/images/homepage/checkout/themed-open.webp";
import imgOldBanks from "../assets/images/homepage/checkout/old-banks.webp";
import imgGrouped from "../assets/images/homepage/checkout/grouped.webp";
import imgGroupedOpen from "../assets/images/homepage/checkout/grouped-open.webp";

// Checkout — a hosted checkout for local payment methods at a payment service
// provider. A short read meant to earn a conversation: the problem, the four
// reinforcing loops behind lost payments (the part told in depth), the
// experiments they became, and the rollout. The state model and the PRDs are
// for the call.
//
// The provider, its merchants and its payment partners stay anonymous. Every
// number is scoped to the intervention that produced it, and the per-
// experiment ranges are never added up. The loops figure is drawn by
// scripts/gen-checkout-loops.js and carries the loops on its own.

const SCREEN = 1624;
const stage = (screens, ratio) => phoneStage(screens.map((src) => [src, SCREEN]), { ratio });

const MERCHANT_FLOW = stage([imgMerchantSite, imgThemedAll, imgThemedOpen], "15 / 8");
const INLINE = stage([imgAll, imgVoucher, imgVoucherDetail]);
const GROUPED = stage([imgOldBanks, imgGrouped, imgGroupedOpen]);

const META = [
  { label: "Role", value: "Designer and product owner (no PM)" },
  { label: "Team", value: "Me and an engineering lead" },
  { label: "Product", value: "A payment provider’s hosted checkout" },
  { label: "When", value: "Redesign from 2025, experiments in 2026" },
];

const OUTCOMES = [
  { value: "+5–6pp", label: "Net blended completion today, after the rollout" },
  { value: "+3–5pp", label: "Completion for returning users" },
  { value: "No rise", label: "In fraud, chargebacks or failures" },
];

const EXPERIMENTS = [
  {
    title: "Completion inline",
    copy: "The chosen method expands in place and generates the voucher or QR code inside the checkout. +1.5–2.5pp completion and 12–18% less checkout time.",
  },
  {
    title: "The merchant’s brand",
    copy: "Brand tokens carry the store’s look into the hosted checkout, where redirect abandonment was concentrated. +2–4pp on redirect flows and 20–25% fewer trust-related tickets.",
  },
  {
    title: "Saved details",
    copy: "Returning users can save non-sensitive details on their device, with consent, instead of retyping them. +3–5pp for returning users.",
  },
  {
    title: "Ranked methods",
    copy: "Most-used methods first, the long tail grouped behind search. Method-selection drop-off down 2–4pp.",
  },
];

const PROOF = [
  "Trust is measurable behavior, not a brand adjective.",
  "Structure moved outcomes further than visual polish did.",
  "Each payment funnel needed its own conversion definition to prove causality.",
];

const NOTES = [
  "The provider, its merchants and its payment partners are anonymised.",
  "Each experiment’s range belongs to that intervention; the ranges are not additive.",
];

function CaseStudyCheckout() {
  return (
    <ResearchArticle
      eyebrow="Case study"
      title="Increasing checkout conversion through systems thinking"
      lead="A hosted checkout kept losing payments to trust, decision load, delayed confirmation and failed retries. I mapped the loops behind those losses and turned the strongest into controlled experiments, with no PM, so I also owned the product requirements."
      outcomes={OUTCOMES}
      meta={META}
      hero={{
        visual: MERCHANT_FLOW,
        alt: "A merchant’s store with the product being bought, a MacBook Air. Next, the hosted checkout in that merchant’s theme, most-used payment methods first. Then Pix open inside the checkout, with its QR code and a Copy code action",
        label: "From the store into its own checkout",
        caption:
          "The merchant’s theme carries through, and the payment completes without leaving the page.",
      }}
      related={relatedArticles("/case-study/checkout")}
    >
      <Section heading="Payments leaked outside the checkout" tocLabel="The problem">
        <Body>
          A payment started on a high-volume merchant’s site and finished in
          this checkout, through local methods: vouchers, bank redirects, cards
          and wallets, mostly on mobile. Method, data entry and completion each
          had a page of their own, and vouchers and redirects often finished
          outside the checkout altogether. People dropped out while choosing,
          after a redirect, and while a payment was being confirmed.
        </Body>
      </Section>

      <Section heading="Four loops explained the behavior" tocLabel="The loops">
        <Body>
          Checkout is a mature pattern, so the need was not in question. What
          was missing was a map of the relationships that decided whether a
          payment completed.
        </Body>
        <Figure
          src={imgCheckoutLoops}
          narrowSrc={imgCheckoutLoopsNarrow}
          alt="Four reinforcing loops, each a cycle of four variables where more of one means more of the next. R1 Trust: status and context clarity, user trust, checkout completion, repeat use and familiarity. R2 Abandonment: visible methods, decision complexity, checkout abandonment, merchants add methods. R3 Pending payment: deferred-method use, confirmation delay, status uncertainty, retries and support load. R4 Error and retry: generic error copy, user confusion, same-method retry, repeated failure"
          label="The four loops"
          caption="Each one amplifies itself, so each one could be turned around."
        />
        <Body>
          Naming them separated polish from changes that could alter how the
          system behaved: what people saw first, when data was asked for, how
          status was explained, and how someone recovered after a delay or a
          failure.
        </Body>
        <Standout>
          The goal: turn payment intent into a completed payment with the least
          possible uncertainty.
        </Standout>
      </Section>

      <Section heading="Four assumptions became experiments" tocLabel="Experiments">
        <Body>
          Each had its own metric, and the team agreed on the primary KPI and
          the guardrails before anything ran.
        </Body>
        <Points items={EXPERIMENTS} />
        <Figure
          visual={INLINE}
          alt="The payment-method list, most used first. Next, Pix selected inline, with its QR code generated in place and a Copy code action. Then its payment details: step-by-step instructions, with Share and Download"
          label="Completion stays in the checkout"
          caption="The method expands, the QR code appears, and the instructions stay on the same page."
        />
        <Figure
          visual={GROUPED}
          alt="The earlier checkout, listing every bank as a payment option of its own. Next, the redesign, with banks grouped under More methods, a search and the first banks. Then Itaú selected inside the group, with its QR code generated in place"
          label="Most-used methods first"
          caption="Before, every bank was an option of its own. After, they sit in one group, with search."
        />
      </Section>

      <Section heading="Rolled out behind guardrails" tocLabel="Results">
        <Body>
          I specified the redesign as states, not screens — pending, completed,
          failed, retry and support for every voucher and redirect outcome — so
          the build did not re-litigate behavior. Exposure was earned: 5% of
          traffic per merchant and payment method for at least two weeks, then
          a country ramp against predefined rollback thresholds. The guardrails
          held, and net blended completion is now up 5–6 points, measured per
          merchant and payment method.
        </Body>
      </Section>

      <Section heading="What this proves" tocLabel="Takeaways">
        <List items={PROOF} />
      </Section>

      <Notes items={NOTES} />
    </ResearchArticle>
  );
}

export default CaseStudyCheckout;
